/* eslint-disable max-lines */
import { useSuspenseQuery } from '@apollo/client';
import { GET_ARBEITSZEIT_BY_ID } from './graphql/queries';
import {
  Abteilung,
  Abwesenheit,
  Abwesenheitsgrund,
  Antrag,
  Arbeitsbereich,
  Arbeitszeit,
  Dienstgang,
  Mitarbeiter,
  Pause,
  Puenktlichkeit,
  Rechtshinweis,
  Zuschlag,
  Audit,
  ArbeitszeitAuditAenderung,
  ArbeitszeitAuditManager,
  Zeitstempel,
  ArbeitszeitenPausenart,
  BackgroundColor,
  Schicht,
} from '../entities';
import {
  ArbeitszeitAuditAenderungsTyp,
  Arbeitszeiten,
} from '../../../gql/graphql';
import { getDateOrNull } from '../../../utils/helperFunctions';
import { Termin, TerminWiederholung } from '../entities/Termin';
import dayjs from 'dayjs';

function calculatePausendauer(arbeitszeit: Arbeitszeiten) {
  let pausendauer = 0;
  for (let i = 0; i < arbeitszeit.pausen.length; i++) {
    if (arbeitszeit.pausen[i].dauer !== null) {
      pausendauer += arbeitszeit.pausen[i].dauer;
    }
  }
  return pausendauer;
}

function calculatePuenktlichkeit(arbeitszeit: Arbeitszeiten) {
  let puenktlichkeit = new Puenktlichkeit();

  if (arbeitszeit.puenktlichkeit !== null) {
    puenktlichkeit = new Puenktlichkeit(
      arbeitszeit.puenktlichkeit.abweichungBeginn === null
        ? null
        : arbeitszeit.puenktlichkeit.abweichungBeginn,
      arbeitszeit.puenktlichkeit.abweichungEnde === null
        ? null
        : arbeitszeit.puenktlichkeit.abweichungEnde,
    );
  }
  return puenktlichkeit;
}

function mapDienstgaenge(arbeitszeit: Arbeitszeiten) {
  return arbeitszeit.dienstgaenge.map((dienstgang) => {
    return {
      id: dienstgang.id,
      beginn: getDateOrNull(dienstgang.beginn),
      ende: getDateOrNull(dienstgang.ende),
      dauer: dienstgang.dauer,
    };
  });
}

function mapPausen(arbeitszeit: Arbeitszeiten): Pause[] {
  return arbeitszeit.pausen.map((pause) => {
    return {
      id: pause.id,
      beginn: getDateOrNull(pause.beginn),
      ende: getDateOrNull(pause.ende),
      dauer: pause.dauer,
      art: ArbeitszeitenPausenart[pause.art],
      bezahlt: pause.bezahlt,
    };
  });
}

function mapRechtshinweise(arbeitszeit: Arbeitszeiten) {
  return arbeitszeit.rechtshinweise.map((rechtshinweis) => {
    return {
      name: rechtshinweis.name,
      art: rechtshinweis.art,
      backgroundcolor: BackgroundColor[rechtshinweis.backgroundcolor],
    };
  });
}

function mapAntrage(arbeitszeit: Arbeitszeiten): Antrag[] {
  return arbeitszeit.antraege.map((antrag) => {
    return {
      id: antrag.id,
      beginn: { time: getDateOrNull(antrag.beginn), isChangeRequested: false },
      ende: { time: getDateOrNull(antrag.ende), isChangeRequested: false },
      aenderungsdatum: getDateOrNull(antrag.aenderungsdatum),
      arbeitszeitId: antrag.arbeitszeitId,
      bemerkungAntragsteller: antrag.bemerkungAntragsteller,
      bemerkungVorgesetzter: antrag.bemerkungVorgesetzter,
      datum: getDateOrNull(antrag.datum),
      erstellungsdatum: getDateOrNull(antrag.erstellungsdatum),
      offen: antrag.offen,
      status: antrag.status,
      mitarbeiter: new Mitarbeiter(
        antrag.mitarbeiter.vorname,
        antrag.mitarbeiter.nachname,
        antrag.mitarbeiter.id,
        false,
        antrag.mitarbeiter.bildUrl,
      ),
      arbeitsbereich: new Arbeitsbereich(
        antrag.arbeitsbereich.name,
        antrag.arbeitsbereich.id,
        antrag.arbeitsbereich.markierung
          ? BackgroundColor[antrag.arbeitsbereich.markierung]
          : '',
        [],
      ),
    };
  });
}

function mapAudit(arbeitszeit: Arbeitszeiten): Audit[] {
  return arbeitszeit.audit.map((audit) => {
    if (audit.__typename === 'Audit') {
      return new Audit(
        audit.id,
        audit.typ,
        ArbeitszeitAuditAenderungsTyp[audit.aenderungsTyp],
        new Date(audit.aenderungsdatum),
        audit.geaendertVon,
        audit.geaendertVonEmail,
        new ArbeitszeitAuditManager(
          audit.manager.email,
          audit.manager.vorname,
          audit.manager.nachname,
        ),
        audit.aenderungen.map(
          (aenderung) =>
            new ArbeitszeitAuditAenderung(
              aenderung.attribut,
              aenderung.nachher,
              aenderung.vorher,
            ),
        ),
      );
    }
  });
}

export function useArbeitszeitById(arbeitszeitId: string): {
  arbeitszeit: Arbeitszeit;
  error?: string;
} {
  const { data, error } = useSuspenseQuery(GET_ARBEITSZEIT_BY_ID, {
    variables: {
      arbeitszeitId: arbeitszeitId,
    },
    fetchPolicy: 'no-cache',
    refetchWritePolicy: 'overwrite',
  });

  if (error) {
    return {
      error: error.message,
      arbeitszeit: null,
    };
  }

  return { arbeitszeit: createArbeitszeit(data.arbeitszeitById), error: null };
}

// eslint-disable-next-line max-lines-per-function, complexity
function createArbeitszeit(arbeitszeit: Arbeitszeiten) {
  const pausendauer = calculatePausendauer(arbeitszeit);
  const puenktlichkeit = calculatePuenktlichkeit(arbeitszeit);
  const dienstgaenge: Dienstgang[] = mapDienstgaenge(arbeitszeit);
  const bemerkung = arbeitszeit.bemerkung ? arbeitszeit.bemerkung : '';
  const pausen: Pause[] = mapPausen(arbeitszeit);
  const pauseneinstellung = arbeitszeit.pauseneinstellung;
  const zuschlaege: Zuschlag[] = [];
  const antraege = mapAntrage(arbeitszeit);
  const rechtshinweise: Rechtshinweis[] = mapRechtshinweise(arbeitszeit);
  const audits: Audit[] = arbeitszeit.audit ? mapAudit(arbeitszeit) : [];
  let abwesenheit: Abwesenheit = null;

  if (arbeitszeit.abwesenheit !== null) {
    abwesenheit = new Abwesenheit(
      arbeitszeit.abwesenheit.id,
      Abwesenheitsgrund[arbeitszeit.abwesenheit.grund],
      arbeitszeit.abwesenheit.dauer,
    );
  }
  if (arbeitszeit.zuschlaege !== null) {
    const tmp = arbeitszeit.zuschlaege.map((zuschlag) => {
      if (zuschlag.betrag.__typename === 'arbeitszeiten_zuschlaege_betrag') {
        return new Zuschlag(
          zuschlag.dauer,
          zuschlag.name,
          zuschlag.prozent,
          zuschlag.betrag.betrag,
        );
      }
      return new Zuschlag(zuschlag.dauer, zuschlag.name, zuschlag.prozent);
    });
    zuschlaege.push(...tmp);
  }

  let termin: Termin | null = null;

  if (arbeitszeit.termin !== null) {
    termin = new Termin(
      arbeitszeit.termin.titel,
      TerminWiederholung[arbeitszeit.termin.wiederholung],
      arbeitszeit.termin.id,
      arbeitszeit.termin.bildUrl,
      arbeitszeit.termin.bildName,
      arbeitszeit.termin.beschreibung,
      getDateOrNull(arbeitszeit.termin?.beginn),
      getDateOrNull(arbeitszeit.termin?.ende),
      arbeitszeit.termin.markierung,
    );
  }
  const az = new Arbeitszeit(
    arbeitszeit.id,
    new Mitarbeiter(
      arbeitszeit.mitarbeiter.vorname,
      arbeitszeit.mitarbeiter.nachname,
      arbeitszeit.mitarbeiter.id,
      arbeitszeit.mitarbeiter.extern,
      arbeitszeit.mitarbeiter.bildUrl,
    ),
    new Arbeitsbereich(
      arbeitszeit.arbeitsbereich.name,
      arbeitszeit.arbeitsbereich.id,
      arbeitszeit.arbeitsbereich.markierung,
      arbeitszeit.arbeitsbereich.abteilungen,
    ),
    new Abteilung(
      arbeitszeit.arbeitsbereich.name,
      arbeitszeit.arbeitsbereich.id,
      arbeitszeit.arbeitsbereich.markierung,
    ),
    arbeitszeit.dauer,
    pausendauer,
    puenktlichkeit,
    pausen,
    pauseneinstellung,
    dienstgaenge,
    [],
    rechtshinweise,
    '',
    getDateOrNull(arbeitszeit.beginn),
    getDateOrNull(arbeitszeit.ende),
    getDateOrNull(arbeitszeit.kommen),
    getDateOrNull(arbeitszeit.gehen),
    abwesenheit,
    bemerkung,
    zuschlaege,
    antraege,
    audits,
    termin,
    arbeitszeit.geaendertVon,
    getDateOrNull(arbeitszeit.aenderungsdatum),
    getDateOrNull(arbeitszeit.datum),
  );
  if (arbeitszeit.beginn2 !== null) {
    az.beginn2 = new Zeitstempel({
      abweichung: arbeitszeit.beginn2.abweichung,
      zeitpunkt: getDateOrNull(arbeitszeit.beginn2.zeitpunkt),
      schicht: arbeitszeit.beginn2.schicht
        ? new Schicht({
            id: arbeitszeit.beginn2.schicht?.id,
            beginn: dayjs(arbeitszeit.beginn2.schicht.beginn),
            ende: dayjs(arbeitszeit.beginn2.schicht.ende),
          })
        : null,
    });
  }
  if (arbeitszeit.ende2 !== null) {
    az.ende2 = new Zeitstempel({
      abweichung: arbeitszeit.ende2.abweichung,
      zeitpunkt: getDateOrNull(arbeitszeit.ende2.zeitpunkt),
      schicht: arbeitszeit.ende2.schicht
        ? new Schicht({
            id: arbeitszeit.ende2.schicht?.id,
            beginn: dayjs(arbeitszeit.ende2.schicht.beginn),
            ende: dayjs(arbeitszeit.ende2.schicht.ende),
          })
        : null,
    });
  }

  if (arbeitszeit.kommen2 !== null) {
    az.kommen2 = new Zeitstempel({
      abweichung: arbeitszeit.kommen2.abweichung,
      zeitpunkt: getDateOrNull(arbeitszeit.kommen2.zeitpunkt),
    });
  }

  if (arbeitszeit.gehen2 !== null) {
    az.gehen2 = new Zeitstempel({
      abweichung: arbeitszeit.gehen2.abweichung,
      zeitpunkt: getDateOrNull(arbeitszeit.gehen2.zeitpunkt),
    });
  }

  return az;
}
